import { TOAST_NEUTRAL_ERROR } from '../constants/errorMessages';

/**
 * True if the string looks like an error code (e.g. UPPER_SNAKE_CASE) and should not be shown in toasts.
 * @param {string} s
 * @returns {boolean}
 */
export function looksLikeErrorCode(s) {
  if (typeof s !== 'string' || !s.trim()) return false;
  const t = s.trim();
  if (/^[A-Z][A-Z0-9_]*$/.test(t) && t.length > 2) return true;
  if (/^[A-Z0-9]+$/.test(t) && t.length > 4) return true;
  if (/^E(RR)?[_0-9]/i.test(t)) return true;
  return false;
}

/**
 * Returns a safe message for toasts: never exposes error codes or [object Object]; uses neutral message when needed.
 * @param {string} message - Raw message from API or error
 * @param {string} [neutralMessage] - Fallback when message looks like a code or is invalid
 * @returns {string}
 */
export function toToastSafeMessage(message, neutralMessage = TOAST_NEUTRAL_ERROR) {
  if (message == null || typeof message !== 'string') return neutralMessage;
  const trimmed = message.trim();
  if (!trimmed || trimmed === '[object Object]' || looksLikeErrorCode(trimmed)) return neutralMessage;
  return trimmed;
}

/**
 * Extracts raw message string from API error (single responsibility).
 * Supports: { response.data.error: { message } }, response.data.message, string error, Error.message.
 * @param {Error & { response?: { data?: { message?: string, error?: string|{ message?: string } } } }} error
 * @returns {string|null}
 */
function getRawMessageFromApiError(error) {
  const data = error.response?.data;
  const errorPayload = data?.error;
  const fromErrorObject =
    errorPayload && typeof errorPayload === 'object' && typeof errorPayload.message === 'string'
      ? errorPayload.message
      : null;
  const raw =
    fromErrorObject ||
    data?.message ||
    (typeof errorPayload === 'string' ? errorPayload : null) ||
    (typeof data === 'string' ? data : null) ||
    error.message;
  return typeof raw === 'string' ? raw : null;
}

/**
 * Normalize API errors in services so callers get a consistent Error with a clear message.
 * Use in catch blocks: throw normalizeServiceError(error);
 * Toast-safe: never exposes error codes; uses neutral message when the API returns a code.
 *
 * @param {Error & { response?: { data?: { message?: string, error?: string|{ message?: string } } } }} error
 * @returns {Error}
 */
export function normalizeServiceError(error) {
  if (!(error instanceof Error)) return new Error(TOAST_NEUTRAL_ERROR);
  const raw = getRawMessageFromApiError(error);
  const resolved = raw ? toToastSafeMessage(raw) : TOAST_NEUTRAL_ERROR;
  return resolved !== error.message ? new Error(resolved) : error;
}
